import axios from 'axios'
import { Bin } from 'chezy-inventory-backend/bin/database/types/Bin'
import { Inventory } from 'chezy-inventory-backend/bin/database/types/Inventory'
import { InventoryItem } from 'chezy-inventory-backend/bin/database/types/InventoryItem'
import { Item } from 'chezy-inventory-backend/bin/database/types/Item'
import React, { useMemo } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { BinWithChildren, mkBinHeirarchy } from '../../utils/createBinTree'
import { getBaseURL } from '../../utils/getBaseURL'

interface IInventoryBrowserContext {
  binTree: BinWithChildren[]
  allBins: Bin[]
  allInventoryMetadatas: Inventory[]
  activeInventoryItems: (InventoryItem & Item)[]
  activeInventoryMetadata: Inventory | undefined
  refresh: () => void
}

export const InventoryBrowserContext = React.createContext<IInventoryBrowserContext>({
  binTree: [],
  allBins: [],
  allInventoryMetadatas: [],
  activeInventoryItems: [],
  activeInventoryMetadata: undefined,
  refresh: () => {
    /* */
  },
})

export const useBinContextProvider = (): IInventoryBrowserContext => {
  const { id: inventoryId } = useParams()
  const [inventoryMetadatas, setInventoryMetadatas] = useState<Inventory[]>()
  const [bins, setBins] = useState<BinWithChildren[]>()
  const [binsFlat, setBinsFlat] = useState<Bin[]>()
  const [items, setItems] = useState<(InventoryItem & Item)[]>()

  const refresh = useCallback(() => {
    if (inventoryId) {
      axios
        .get(`${getBaseURL()}/inventories/${inventoryId}/items`)
        .then((data) => setItems(data.data as (InventoryItem & Item)[]))
    }
  }, [inventoryId])

  useEffect(() => {
    axios.get(`${getBaseURL()}/inventories`).then((data) => setInventoryMetadatas(data.data as Inventory[]))
    axios.get(`${getBaseURL()}/bins`).then((data) => {
      setBins(mkBinHeirarchy(data.data as Bin[]))
      setBinsFlat(data.data as Bin[])
    })
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const activeInventoryMetadata = useMemo(
    () => inventoryMetadatas?.find((inv) => inv.id === inventoryId),
    [inventoryId, inventoryMetadatas]
  )

  return {
    binTree: bins ?? [],
    allBins: binsFlat ?? [],
    allInventoryMetadatas: inventoryMetadatas ?? [],
    activeInventoryItems: items ?? [],
    activeInventoryMetadata,
    refresh,
  }
}

export const useInventoryContext = () => React.useContext(InventoryBrowserContext)
